import { Outlet, createFileRoute, useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { Spinner } from '@/src/components/ui/spinner'
import { me } from '@/src/api/user'
import { getOperationType } from '@/src/api/operation-type'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import { DynamicForm } from '@/src/components/manage/forms/DynamicForm'

export const Route = createFileRoute('/_manage/manage/organization/operation-types_/$typeId')({
  component: OrgOperationTypeViewPage,
})

function OrgOperationTypeViewPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { typeId } = useParams({ from: '/_manage/manage/organization/operation-types_/$typeId' })
  const [mode, setMode] = useState<'basic' | 'expert'>('basic')

  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)
  const orgId = user?.organization?.id

  const { data: operationType, isLoading: isTypeLoading } = useQuery({
    queryKey: ['operationType', typeId],
    queryFn: () => getOperationType({ data: { id: typeId } }),
    enabled: !!typeId,
  })

  useEffect(() => {
    if (!isUserLoading && !canManageOrg) {
      navigate({ to: '/not-permitted' })
      return
    }

    if (!isUserLoading && canManageOrg && !orgId) {
      navigate({ to: '/manage/organization/operation-types' })
      return
    }

    if (!isTypeLoading && operationType) {
      const isGlobalType = !operationType.organizationId
      const isOwnOrgType = operationType.organizationId === orgId
      if (!isGlobalType && !isOwnOrgType) {
        navigate({ to: '/manage/organization/operation-types' })
      }
    }
  }, [isUserLoading, isTypeLoading, canManageOrg, orgId, operationType, navigate])

  if (location.pathname.endsWith('/edit')) {
    return <Outlet />
  }

  if (isUserLoading || isTypeLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!operationType) {
    return (
      <div className="p-6 text-muted-foreground">
        Operation type not found.
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>
            {operationType.labels?.en || operationType.name}
          </AdminToolbar.Title>
          {operationType.organizationId === orgId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate({
                  to: '/manage/organization/operation-types/$typeId/edit',
                  params: { typeId: operationType.id },
                })
              }
            >
              Edit
            </Button>
          )}
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <div className="flex items-center gap-2">
            <Button
              variant={mode === 'basic' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('basic')}
            >
              Basic
            </Button>
            <Button
              variant={mode === 'expert' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('expert')}
            >
              Expert
            </Button>
          </div>
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto">
          <DynamicForm
            schema={operationType.formSchema}
            locale="en"
            mode={mode}
            onModeChange={setMode}
            showModeToggle={false}
            readOnly={true}
            showActions={false}
            onSubmit={() => undefined}
          />
        </div>
      </div>
    </div>
  )
}
